import React, {useContext, useRef} from 'react'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProgramListResult from '../../controllers/LimeProgramListResult'
import PagedSearchTable, {PagedTableFunctions} from "../../components/PagedSearchTable"
import Warning, {useWarningState} from "../program/Warning"
import {showSuccessOrFailed} from "../../Snacks"
import {dateFormat} from "../../date"
import PermissionEnum from "../../controllers/PermissionEnum"
import Tabs from "../../components/Tabs"
import {useNavigate} from "react-router-dom"
import Success from "../../components/Success"

const LimingPrograms: React.FC = () => {
    const context = useContext(AppContext)

    return (
        <Tabs tabs={[
            {
                header: () => context.word('current'),
                key: 'current',
                content: <LimeProgramList key="current1" />
            },
            {
                header: () => context.word('archives'),
                key: 'archives',
                content: <LimeProgramArchives key="archive1" />
            },
        ]} />
    )
}
export default LimingPrograms

const LimeProgramDownload: React.FC<{
    id: number;
    downloadExcel: (id: number) => void;
}> = (props) => {
    return (
        <div className="flex">
            <div className="flex ml-3">
                <div
                    className="w-[48px] h-[39px] bg-no-repeat cursor-pointer hover:bg-gray-200"
                    style={{ backgroundImage: 'url(/images/excel.png)' }}
                    onClick={(e) => {
                        e.stopPropagation()
                        props.downloadExcel(props.id)
                    }}
                />
            </div>
        </div>
    )
}

const LimeProgramList: React.FC = () => {
    const navigate = useNavigate()
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProgramListResult>>()
    const warningState = useWarningState<number>(-1)

    function rowRemove(id: number) {
        showSuccessOrFailed(context, LimeController.archiveProgram({ id }), 'successfully_deleted', 'failed_to_delete').then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    function rowRemoveWarning(id: number) {
        warningState.show(`${context.word('removing')}?`, id)
    }

    function programClick(row: LimeProgramListResult) {
        navigate(`/liming/programs/${row.id}`)
    }

    function downloadExcel(id: number) {
        context.showSnack(<Success title={context.word('download_start_shortly')} />)
        window.location.href = LimeController.urls.getProgramExcel(id)
    }

    return <>
        <Warning state={warningState} onYes={rowRemove} />

        <div className="btn bg-primary m-2"
            onClick={() => navigate('/liming/programs/new')}>{context.word('new_program')}</div>

        <PagedSearchTable<LimeProgramListResult>
            call={LimeController.programsPaged}
            rowClick={programClick}
            componentRef={pagedTableRef}
            columns={[
                {
                    header: context.word('name'),
                    row: (item) => item.name
                },
                {
                    header: context.word('farm'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.farmName}</div>
                            <div className="text-xs text-gray-500">{item.clientName}</div>
                        </>
                    )
                },
                {
                    header: context.word('template'),
                    row: (item) => item.templateName
                },
                {
                    header: context.word('created'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.createdBy}</div>
                            <div className="text-xs text-gray-500">{dateFormat(item.createdOn)}</div>
                        </>
                    )
                },
                {
                    header: context.word('updated'),
                    row: (item) => dateFormat(item.updatedOn)
                },
                {
                    header: context.word('submitted'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.submittedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.submittedOn ? dateFormat(item.submittedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('approved'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.approvedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.approvedOn ? dateFormat(item.approvedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('actions'),
                    row: (item) =>
                        <>
                            <div className="flex items-center">
                                {context.hasPermission(PermissionEnum.LimingProgramsPermission)
                                    ? <>
                                        <span className="underline" onClick={event => {
                                            event.stopPropagation()
                                            rowRemoveWarning(item.id)
                                        }}>{context.word('delete')}</span>
                                    </>
                                    : null
                                }

                                {item.approvedOn !== null && (
                                    <span onClick={e => e.stopPropagation()}>
                                        <LimeProgramDownload
                                            id={item.id}
                                            downloadExcel={downloadExcel}
                                        />
                                    </span>
                                )}
                            </div>
                        </>
                }
            ]}
            keyExtractor={i => i.id}
        />
    </>
}

const LimeProgramArchives: React.FC = () => {
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProgramListResult>>()
    const warningState = useWarningState<number>(-1)

    function rowRestore(id: number) {
        showSuccessOrFailed(context, LimeController.restoreProgram({ id }), 'successfully_restored', 'failed_to_restore').then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    function rowRemoveWarning(id: number) {
        warningState.show(`${context.word('restore')}?`, id)
    }

    return <>
        <Warning state={warningState} onYes={rowRestore} />

        <PagedSearchTable<LimeProgramListResult>
            instantSearch={true}
            call={LimeController.programArchives}
            componentRef={pagedTableRef}
            columns={[
                {
                    header: context.word('name'),
                    row: (item) => item.name
                },
                {
                    header: context.word('farm'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.farmName}</div>
                            <div className="text-xs text-gray-500">{item.clientName}</div>
                        </>
                    )
                },
                {
                    header: context.word('template'),
                    row: (item) => item.templateName
                },
                {
                    header: context.word('created'),
                    row: (item) => (
                        <>
                            <div className="text-l text-gray-800">{item.createdBy}</div>
                            <div className="text-xs text-gray-500">{dateFormat(item.createdOn)}</div>
                        </>
                    )
                },
                {
                    header: context.word('updated'),
                    row: (item) => dateFormat(item.updatedOn)
                },
                {
                    header: context.word('submitted'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.submittedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.submittedOn ? dateFormat(item.submittedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('approved'),
                    row: (item) =>
                        <>
                            <div className="text-l text-gray-800">{item.approvedBy || '-'}</div>
                            <div className="text-xs text-gray-500">{item.approvedOn ? dateFormat(item.approvedOn) : ''}</div>
                        </>
                },
                {
                    header: context.word('actions'),
                    row: (item) =>
                        <>
                            <div className="flex items-center">
                                {context.hasPermission(PermissionEnum.LimingProgramsPermission)
                                    ? <span className="underline mx-3" onClick={event => {
                                        event.stopPropagation()
                                        rowRemoveWarning(item.id)
                                    }}>{context.word('restore')}</span>
                                    : null
                                }
                            </div>
                        </>
                }
            ]}
            keyExtractor={i => i.id} />
    </>
}
